import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import axios, { AxiosResponse } from 'axios';
import { Formik, FormikHelpers } from 'formik';
import {
  Alert, Button, Col, Form, Row,
} from 'react-bootstrap';
import { uniqueId } from 'lodash';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import { ApiV1Error, isCSRFError, isValidationError } from '../../../../structures/responses/axios';
import routes from '../../../../utils/routes';
import { loginValidation } from '../validation';
import { GTMEvent } from '../../../../structures/responses/gtm';

type MainLoginProps = {
  // Leave undefined to disable forgot password functionality
  onForgotPassword?: (email?: string) => void;
  setIsLoading?: (isLoading: boolean) => void;
  className?: string;
  // Set true to enable remember me
  rememberMe?: boolean;
};

type LoginValues = {
  email: string;
  password: string;
  remember?: boolean;
};

const MainLogin: React.FC<MainLoginProps> = (props) => {
  const {
    onForgotPassword,
    setIsLoading = (isLoading: boolean) => undefined,
    className = '',
    rememberMe = false,
  } = props;
  const [redirect, setRedirect] = useState({
    shouldRedirect: true,
    userHasLoggedIn: false,
    redirectTo: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [submissionValid, setSubmissionValid] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { width } = useWindowDimensions();

  const emailId = uniqueId('email_');
  const passwordId = uniqueId('password_');

  useEffect(() => {
    $(() => {
      $('#loginModal')
        .on('show.bs.modal', (event) => {
          // @ts-ignore next-line
          const button = $(event.relatedTarget);
          if (button) {
            const noredirect = button.data('noredirect') || false;
            setRedirect((currentRedirect) => ({
              ...currentRedirect,
              shouldRedirect: !noredirect,
            }));
          } else {
            // Default to redirect
            setRedirect((currentRedirect) => ({
              ...currentRedirect,
              shouldRedirect: true,
            }));
          }
        });
    });
    return () => {
      $('#loginModal')
        .off('show.bs.modal');
    };
  }, []);

  const redirectOnLogin = useCallback(() => {
    setRedirect((currentRedirect) => ({
      ...currentRedirect,
      userHasLoggedIn: true,
    }));
  }, []);

  useEffect(() => {
    document.addEventListener('userLoggedIn', redirectOnLogin);

    return () => {
      document.removeEventListener('userLoggedIn', redirectOnLogin);
    };
  }, []);

  useEffect(() => {
    const {
      userHasLoggedIn,
      redirectTo,
    } = redirect;

    if (userHasLoggedIn && redirectTo) {
      window.location.replace(redirectTo);
    }
  }, [redirect]);

  // Defines whether the form input should autofocus or not
  const autoFocus = width > 991;

  const initialValues: LoginValues = {
    email: '',
    password: '',
    remember: false,
  };

  const onLogin = useCallback((
    values: LoginValues,
    {
      setErrors,
      setSubmitting,
    }: FormikHelpers<LoginValues>,
  ) => {
    setSubmitting(true);
    setIsLoading(true);
    setErrorMessage('');

    // Massage data for submission
    const loginValues = {
      ...values,
      redirect: redirect.shouldRedirect,
    };
    if (!loginValues.remember) {
      delete loginValues.remember;
    }

    axios.post(routes.API_LOGIN_POST, loginValues)
      .then((res: AxiosResponse<{ redirectTo: string, gtmEvents: GTMEvent[] }>) => {
        setSubmissionValid(true);
        // If no GTM events are submitted with the response,
        // we should not wait for the `userLoggedIn` callback
        let hasLoggedIn = false;
        if (!res.data.gtmEvents?.some((gtmEvent) => gtmEvent.event === 'userLoggedIn')) {
          hasLoggedIn = true;
        }
        setRedirect((currentRedirect) => ({
          ...currentRedirect,
          userHasLoggedIn: hasLoggedIn || currentRedirect.userHasLoggedIn,
          redirectTo: res.data.redirectTo ?? routes.ADMIN_INDEX,
        }));
      })
      .catch((error: ApiV1Error) => {
        if (error.response?.data?.tfaRequired) {
          window.location.href = routes.TFA_FORM;
        } else if (isValidationError(error)) {
          setErrors(error.response.data.errors);
        } else if (isCSRFError(error)) {
          setErrorMessage('This form must be refreshed. Please try again.');
        } else {
          setErrorMessage('An unknown error occurred. Please try again.');
        }
      })
      .finally(() => {
        setSubmitting(false);
        setIsLoading(false);
      });
  }, [redirect]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loginValidation}
      onSubmit={onLogin}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {(formikProps) => {
        const {
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        } = formikProps;

        return (
          <div className={`main-login ${className}`}>
            <div className="text-center">
              <p className="text-muted">Welcome back! Please log in to your account.</p>
            </div>

            {errorMessage && (
              <Alert variant="danger">
                {errorMessage}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Form.Group controlId={emailId}>
                <Form.Control
                  type="email"
                  name="email"
                  aria-label="Email address."
                  placeholder="Email"
                  value={values.email}
                  autoComplete="email"
                  required
                  autoFocus={autoFocus}
                  isInvalid={touched.email && !!errors.email}
                  isValid={submissionValid}
                  onChange={handleChange('email')}
                  onBlur={handleBlur('email')}
                />
                <Form.Control.Feedback type="invalid">
                  {touched.email && errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId={passwordId} className="position-relative">
                <Form.Control
                  name="password"
                  aria-label="Password."
                  placeholder="Password"
                  value={values.password}
                  autoComplete="current-password"
                  required
                  isInvalid={touched.password && !!errors.password}
                  isValid={submissionValid}
                  onChange={handleChange('password')}
                  onBlur={handleBlur('password')}
                  type={passwordVisible ? 'text' : 'password'}
                />
                <Form.Text
                  className="end-0 top-0 form-text position-absolute"
                >
                  <Button
                    variant="link"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    <FontAwesomeIcon
                      className={passwordVisible ? 'text-primary' : 'text-muted'}
                      icon={passwordVisible ? faEyeSlash : faEye}
                    />
                  </Button>
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {touched.password && errors.password}
                </Form.Control.Feedback>
              </Form.Group>

              <Row>
                <Col md={6}>
                  {rememberMe && (
                    <Form.Group controlId="remember">
                      <Form.Check
                        label="Remember me"
                        type="checkbox"
                        checked={values.remember}
                        onChange={handleChange('remember')}
                        isInvalid={touched.remember && !!errors.remember}
                      />
                      <Form.Control.Feedback type="invalid">
                        {touched.remember && errors.remember}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}
                </Col>
                <Col md={6} className="text-right">
                  {!!onForgotPassword && (
                    <Button
                      variant="link"
                      type="button"
                      className="text-muted small-txt p-0 border-0 shadow-none align-top mb-3"
                      onClick={() => onForgotPassword(values.email)}
                    >
                      Forgot Password
                    </Button>
                  )}
                </Col>
              </Row>

              <Button
                variant="primary"
                type="submit"
                block
                disabled={isSubmitting}
              >
                Login
                {isSubmitting && (
                  <span className="spinner-border spinner-border-sm my-auto ml-3">
                    <span className="sr-only">Loading...</span>
                  </span>
                )}
              </Button>
            </form>
          </div>
        );
      }}
    </Formik>
  );
};

export default MainLogin;
